import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createBrowserUserProfileRepository,
  type UserProfile,
  type UserProfileRepository,
} from '../lib/userProfile';

interface UserProfileState {
  profile: UserProfile | null;
  loading: boolean;
  loadError: string | null;
  retryLoad(): void;
  refreshProfile(): void;
  createProfile(displayName: string): UserProfile;
  updateDisplayName(displayName: string): UserProfile;
}

const UserProfileContext = createContext<UserProfileState | null>(null);

export function UserProfileProvider({
  children,
  repository,
}: {
  children: ReactNode;
  repository?: UserProfileRepository;
}): JSX.Element {
  const profileRepository = useMemo(
    () => repository ?? createBrowserUserProfileRepository(),
    [repository],
  );
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    try {
      setProfile(profileRepository.load());
      setLoadError(null);
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : 'The local profile could not be read.',
      );
    } finally {
      setLoading(false);
    }
  }, [profileRepository, loadAttempt]);

  const createProfile = useCallback(
    (displayName: string) => {
      const next = profileRepository.create(displayName);
      setProfile(next);
      return next;
    },
    [profileRepository],
  );

  const updateDisplayName = useCallback(
    (displayName: string) => {
      if (!profile) throw new Error('No local profile is available.');
      const next = profileRepository.update(profile, displayName);
      setProfile(next);
      return next;
    },
    [profile, profileRepository],
  );

  const refreshProfile = useCallback(() => {
    setProfile(profileRepository.load());
    setLoadError(null);
  }, [profileRepository]);

  return (
    <UserProfileContext.Provider
      value={{
        profile,
        loading,
        loadError,
        retryLoad: () => {
          setLoading(true);
          setLoadAttempt((value) => value + 1);
        },
        refreshProfile,
        createProfile,
        updateDisplayName,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile(): UserProfileState {
  const value = useContext(UserProfileContext);
  if (!value) throw new Error('useUserProfile must be used inside UserProfileProvider.');
  return value;
}
